import { Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { AbstractControl, AbstractControlOptions, FormControl, FormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { catchError, throwError } from 'rxjs';
import { Temporal } from 'temporal-polyfill';
import { AppscriptsSettingParsed, AppscriptsSettingUpdate, parsedValueToString, parseValue } from '../../../shared/models/appscripts-setting.model';
import { AppscriptsSettingService } from '../../../shared/services/appscripts-setting.service';
import { ModalService } from '../../../shared/services/modal.service';
import { NotificationService } from '../../../shared/services/notification.service';
import { Validators as V, ValidationService } from '../../../shared/services/validation.service';

@Component({
  standalone: false,
  selector: 'app-appscripts-setting-index-component',
  templateUrl: './appscripts-setting-index.component.html',
  styleUrls: ['./appscripts-setting-index.component.scss'],
})
export class AppscriptsSettingIndexComponent implements OnInit {
  loading = false;

  form: FormGroup;
  settingsMap: Record<string, AppscriptsSettingParsed> = {};
  durationTotalMap: Record<string, Temporal.TotalUnit<Temporal.DateTimeUnit>> = {
    'networks-popularity.numbers-allowed-offset': 'days',
    'networks-popularity.mo.avg-tests-offset': 'days',
    'networks-popularity.cli.avg-tests-offset': 'days',
    'activation-campaign.attempts-limit-offset': 'days',
    'incentive-campaign.attempt-interval': 'hours',
    'deactivation-campaign.last-sms-offset': 'hours',
    'registration-limits.evaluation-period': 'days',
    'suspicious-campaign.lookback-window-offset': 'hours',
    'suspicious-campaign.problematic-countries-lookback-window-offset': 'hours',
    'suspicious-campaign.test-ttl': 'minutes',
  };

  layout = LAYOUT;
  changesCount = 0;

  @ViewChild('saveModalTpl', { read: TemplateRef, static: false }) saveModalTpl: any;

  constructor(
    private service: AppscriptsSettingService,
    titleService: Title,
    private notificationService: NotificationService,
    public validationService: ValidationService,
    public modal: ModalService
  ) {
    titleService.setTitle('Appscripts settings');
  }

  ngOnInit() {
    this.update();
  }

  update() {
    this.loading = true;
    this.service
      .all()
      .pipe(
        catchError(e => {
          this.loading = false;
          this.notificationService.error({
            title: 'Settings',
            message: 'An error occurred while loading settings',
            serviceName: 'APPSCRIPTS',
            requestMessage: e.statusText,
            requestCode: e.status,
          });
          return throwError(() => e);
        })
      )
      .subscribe(settings => {
        const controls: Record<string, FormControl> = {};
        const controlOptions = this.getControlOptions();
        settings.forEach(s => {
          const parsed = {
            id: s.id,
            value: s.value,
            newValue: s.value,
            valueType: s.valueType,
            valueParsed: parseValue(s.value, s.valueType),
            hasChanges: false,
          };
          this.settingsMap[s.id] = parsed;
          if (!controlOptions[s.id]) {
            console.error('Not found validators ' + s.id);
          }
          controls[s.id] = new FormControl(this.extractValue(parsed), controlOptions[s.id]);
        });
        this.form = new FormGroup(controls);
        this.form.statusChanges.subscribe(status => {
          if (status === 'INVALID') {
            console.log(ValidationService.getInvalidControls(this.form));
          }
        })
        this.loading = false;
      });
  }

  onClickSave() {
    const dialogRef = this.modal.alert().size('sm').component(this.saveModalTpl).open();
    dialogRef.result
      .then(result => {
        if (result) {
          this.saveChanges();
        }
      })
      .catch(_ => {});
  }

  private saveChanges() {
    const updates: AppscriptsSettingUpdate[] = Object.values(this.settingsMap)
      .filter(_ => _.hasChanges)
      .map(s => {
        return {
          id: s.id,
          value: s.newValue,
        };
      });
    this.loading = true;
    this.service
      .save(updates)
      .pipe(
        catchError(e => {
          this.loading = false;
          this.notificationService.error({
            title: 'Settings',
            message: 'An error occurred while saving settings',
            serviceName: 'APPSCRIPTS',
            requestMessage: e.statusText,
            requestCode: e.status,
          });
          return throwError(() => e);
        })
      )
      .subscribe(() => {
        this.loading = false;
        this.notificationService.success('Setting updated', 'Appscripts');
        this.changesCount = 0;
        Object.values(this.settingsMap).forEach(s => {
          s.hasChanges = false;
          s.value = s.newValue;
        });
      });
  }

  onChangeSetting(model: AppscriptsSettingParsed, formControl: AbstractControl) {
    if (!formControl.valid) {
      console.log(ValidationService.getInvalidControls(this.form));
      return;
    }
    let newValueParsed = formControl.value;
    if (model.valueType === 'DURATION') {
      const unit = this.durationTotalMap[model.id];
      const params = {};
      if (model.id === 'incentive-campaign.attempt-interval') {
        params['seconds'] = Math.round(Number(newValueParsed) * 3600);
      } else {
        params[unit] = parseInt(newValueParsed);
      }
      newValueParsed = Temporal.Duration.from(params);
    }
    const newValue = parsedValueToString(newValueParsed, model.valueType);
    if (newValue !== model.value) {
      model.hasChanges = true;
      model.newValue = newValue;
    } else {
      model.hasChanges = false;
      model.newValue = model.value;
    }
    const changes = Object.values(this.settingsMap).filter(_ => _.hasChanges);
    this.changesCount = changes.length;
  }

  private extractValue(parsed: AppscriptsSettingParsed): any {
    if (parsed.valueType === 'DURATION') {
      const val = parsed.valueParsed as Temporal.Duration;
      return val.total({ unit: this.durationTotalMap[parsed.id] });
    }
    return parsed.valueParsed;
  }

  private getControlOptions(): Record<string, AbstractControlOptions> {
    const integerValidator: ValidatorFn = control => (/^\d+$/.test(String(control.value)) ? null : { integer: true });
    return {
      'networks-popularity.min-users': { validators: [V.required, V.min(1), this.maxValidator('networks-popularity.max-users')] },
      'networks-popularity.max-users': { validators: [V.required, V.min(10), V.max(1000), this.minValidator('networks-popularity.min-users')] },
      'networks-popularity.numbers-allowed-offset': { validators: [V.required, V.min(7), V.max(365)] },
      'networks-popularity.numbers-allowed-multiplier': { validators: [V.required, V.min(0.1), V.max(3)] },
      'networks-popularity.mo.min-users': { validators: [V.required, V.min(1), this.maxValidator('networks-popularity.mo.max-users')] },
      'networks-popularity.mo.max-users': { validators: [V.required, V.min(1), V.max(1000), this.minValidator('networks-popularity.mo.min-users')] },
      'networks-popularity.mo.avg-tests-offset': { validators: [V.required, V.min(7), V.max(365)] },
      'networks-popularity.mo.avg-tests-multiplier': { validators: [V.required, V.min(0.1), V.max(3)] },
      'networks-popularity.cli.min-users': { validators: [V.required, V.min(1), this.maxValidator('networks-popularity.cli.max-users')] },
      'networks-popularity.cli.max-users': { validators: [V.required, V.min(1), V.max(1000), this.minValidator('networks-popularity.cli.min-users')] },
      'networks-popularity.cli.avg-tests-offset': { validators: [V.required, V.min(7), V.max(365)] },
      'networks-popularity.cli.avg-tests-multiplier': { validators: [V.required, V.min(0.1), V.max(3)] },
      'activation-campaign.ai-min': { validators: [V.required, V.min(1), V.max(180)] },
      'activation-campaign.attempts-limit-offset': { validators: [V.required, V.integer(true)] },
      'activation-campaign.attempts-limit': { validators: [V.required, V.integer(true)] },
      'incentive-campaign.ported-max-enabled-numbers': {
        validators: [V.required, integerValidator, V.min(0), V.max(2147483647)],
      },
      'incentive-campaign.top-networks-pct': {
        validators: [V.required, integerValidator, V.min(0), V.max(100)],
      },
      'incentive-campaign.incentive-window-days': {
        validators: [V.required, integerValidator, V.min(1), V.max(365)],
      },
      'incentive-campaign.sms-per-number-per-day': {
        validators: [V.required, integerValidator, V.min(0), V.max(1000)],
      },
      'incentive-campaign.attempt-interval': { validators: [V.required, V.min(10 / 60), V.max(365 * 24)] },
      'maintenance-campaign.max-tests-by-network': { validators: [V.required, V.min(10), V.max(100)] },
      'maintenance-campaign.mo-mcc-list': { validators: [V.required] },
      'deactivation-campaign.last-sms-offset': { validators: [V.required, V.min(24), V.max(168)] },
      'phone-blocker.wi-rules.1.numbers': { validators: [V.required, V.min(10), this.maxValidator('phone-blocker.wi-rules.2.numbers')] },
      'phone-blocker.wi-rules.1.wi': { validators: [V.required, this.minValidator('phone-blocker.wi-rules.2.wi')] },
      'phone-blocker.wi-rules.2.numbers': {
        validators: [V.required, this.minValidator('phone-blocker.wi-rules.1.numbers'), this.maxValidator('phone-blocker.wi-rules.3.numbers')],
      },
      'phone-blocker.wi-rules.2.wi': {
        validators: [V.required, this.maxValidator('phone-blocker.wi-rules.1.wi'), this.minValidator('phone-blocker.wi-rules.3.wi')],
      },
      'phone-blocker.wi-rules.3.numbers': { validators: [V.required, V.max(100), this.minValidator('phone-blocker.wi-rules.2.numbers')] },
      'phone-blocker.wi-rules.3.wi': { validators: [V.required, V.min(100), this.maxValidator('phone-blocker.wi-rules.2.wi')] },
      'registration-limits.evaluation-period': { validators: [V.required, V.min(1), V.max(365)] },
      'registration-limits.completed-limit': { validators: [V.required, V.min(1), V.max(1000)] },
      'registration-limits.verified-limit': { validators: [V.required, V.min(1), V.max(1000)] },
      'suspicious-campaign.min-numbers-in-window': { validators: [V.required, V.min(1), V.max(20)] },
      'suspicious-campaign.lookback-window-offset': { validators: [V.required, V.min(1), V.max(168)] },
      'suspicious-campaign.min-distinct-users': { validators: [V.required, V.min(1), V.max(20)] },
      'suspicious-campaign.min-tests-for-check-delivery': { validators: [V.required, V.min(1), V.max(20)] },
      'suspicious-campaign.problematic-countries-min-numbers-in-window': { validators: [V.required, V.min(1), V.max(20)] },
      'suspicious-campaign.problematic-countries-lookback-window-offset': { validators: [V.required, V.min(1), V.max(168)] },
      'suspicious-campaign.problematic-countries-min-distinct-users': { validators: [V.required, V.min(1), V.max(20)] },
      'suspicious-campaign.problematic-countries-min-tests-for-check-delivery': { validators: [V.required, V.min(1), V.max(20)] },
      'suspicious-campaign.problematic-countries': { validators: [V.required] },
      'suspicious-campaign.excluded-users': { validators: [V.required] },
      'suspicious-campaign.excluded-suppliers': { validators: [V.required] },
      'suspicious-campaign.test-ttl': { validators: [V.required, V.min(1), V.max(60)] },
    };
  }

  getChangesSettings(): ChangesSettings[] {
    const changesSettings: ChangesSettings[] = [];
    this.layout.forEach(pane => {
      const paneTitle = pane.title;
      const controls = [];
      pane.columns.forEach(column => {
        const labelMap: Record<string, string> = {};
        column.controls.forEach(c => (labelMap[c.id] = c.label));
        const changes = Object.keys(labelMap)
          .map(id => this.settingsMap[id])
          .filter(_ => _.hasChanges);
        if (changes.length) {
          changes.forEach(c => {
            controls.push({
              label: labelMap[c.id],
              parsed: c,
            });
          });
        }
      });
      if (controls.length) {
        changesSettings.push({
          title: paneTitle,
          controls: controls,
        });
      }
    });
    return changesSettings;
  }

  private minValidator = (relatedControlName: string, skipEmpty?: boolean): ValidatorFn => {
    return (control: FormControl): ValidationErrors => {
      if (!this.form) {
        return null;
      }
      const related = this.form.controls[relatedControlName];
      const min = Number(related.value);
      const v = Number(control.value);
      return (skipEmpty && !v) || v > min ? null : { min: { min: min + 1 } };
    };
  };

  private maxValidator = (relatedControlName: string, skipEmpty?: boolean): ValidatorFn => {
    return (control: FormControl): ValidationErrors => {
      if (!this.form) {
        return null;
      }
      const related = this.form.controls[relatedControlName];
      const max = Number(related.value);
      const v = Number(control.value);
      return (skipEmpty && !v) || v < max ? null : { max: { max: max - 1 } };
    };
  };
}

interface ChangesSettings {
  title: string;
  controls: { label: string; parsed: AppscriptsSettingParsed }[];
}

interface LayoutPane {
  className?: string;
  title: string;
  columns: LayoutColumn[];
}

interface LayoutColumn {
  className?: string;
  controls: LayoutControl[];
}

interface LayoutControl {
  className?: string;
  id: string;
  label: string;
}

const LAYOUT: LayoutPane[] = [
  {
    title: 'Registrations and limits (by network)',
    columns: [
      {
        className: 'col-4',
        controls: [
          {
            id: 'networks-popularity.min-users',
            label: 'Min users',
          },
          {
            id: 'networks-popularity.max-users',
            label: 'Max users',
          },
        ],
      },
      {
        className: 'col-4',
        controls: [
          {
            id: 'networks-popularity.numbers-allowed-offset',
            label: 'Days to count tests for limits',
          },
          {
            id: 'networks-popularity.numbers-allowed-multiplier',
            label: 'Multiplier for limits',
          },
        ],
      },
    ],
  },
  {
    title: 'MO limits (by network)',
    columns: [
      {
        className: 'col-4',
        controls: [
          {
            id: 'networks-popularity.mo.min-users',
            label: 'Min users',
          },
          {
            id: 'networks-popularity.mo.max-users',
            label: 'Max users',
          },
        ],
      },
      {
        className: 'col-4',
        controls: [
          {
            id: 'networks-popularity.mo.avg-tests-offset',
            label: 'Days to count tests for limits',
          },
          {
            id: 'networks-popularity.mo.avg-tests-multiplier',
            label: 'Multiplier for limits',
          },
        ],
      },
    ],
  },
  {
    title: 'CLI limits (by network)',
    columns: [
      {
        className: 'col-4',
        controls: [
          {
            id: 'networks-popularity.cli.min-users',
            label: 'Min users',
          },
          {
            id: 'networks-popularity.cli.max-users',
            label: 'Max users',
          },
        ],
      },
      {
        className: 'col-4',
        controls: [
          {
            id: 'networks-popularity.cli.avg-tests-offset',
            label: 'Days to count tests for limits',
          },
          {
            id: 'networks-popularity.cli.avg-tests-multiplier',
            label: 'Multiplier for limits',
          },
        ],
      },
    ],
  },
  {
    title: 'Registrations and limits (by IP)',
    columns: [
      {
        className: 'col-4',
        controls: [
          {
            id: 'registration-limits.evaluation-period',
            label: 'Evaluation period (days)',
          },
        ],
      },
      {
        className: 'col-4',
        controls: [
          {
            id: 'registration-limits.verified-limit',
            label: 'Verified limit',
          },
        ],
      },
      {
        className: 'col-4',
        controls: [
          {
            id: 'registration-limits.completed-limit',
            label: 'Completed limit',
          },
        ],
      },
    ],
  },
  {
    title: 'Activation campaign',
    columns: [
      {
        className: 'col-4',
        controls: [
          {
            id: 'activation-campaign.ai-min',
            label: 'Min AI for activation',
          },
        ],
      },
      {
        className: 'col-4',
        controls: [
          {
            id: 'activation-campaign.attempts-limit-offset',
            label: 'Attempts limit offset (days)',
          },
        ],
      },
      {
        className: 'col-4',
        controls: [
          {
            id: 'activation-campaign.attempts-limit',
            label: 'Attempts limit',
          },
        ],
      },
    ],
  },
  {
    title: 'Incentive campaign',
    columns: [
      {
        className: 'col-3',
        controls: [{ id: 'incentive-campaign.top-networks-pct', label: 'Top priority networks (%)' }],
      },
      {
        className: 'col-3',
        controls: [{ id: 'incentive-campaign.incentive-window-days', label: 'Window from registration (days)' }],
      },
      {
        className: 'col-3',
        controls: [{ id: 'incentive-campaign.sms-per-number-per-day', label: 'Sent tests per number per UTC day' }],
      },
      {
        className: 'col-3',
        controls: [{ id: 'incentive-campaign.attempt-interval', label: 'Attempt interval (hours)' }],
      },
      {
        className: 'col-3',
        controls: [
          {
            id: 'incentive-campaign.ported-max-enabled-numbers',
            label: 'Maximum other enabled numbers on a ported network',
          },
        ],
      },
    ],
  },
  {
    title: 'Maintenance campaign',
    columns: [
      {
        className: 'col-4',
        controls: [
          {
            id: 'maintenance-campaign.max-tests-by-network',
            label: 'Messages limit per network',
          },
        ],
      },
      {
        className: 'col-8 mcc-list',
        controls: [
          {
            id: 'maintenance-campaign.mo-mcc-list',
            label: 'MO countries (MCC)',
          },
        ],
      },
    ],
  },
  {
    title: 'Suspicious campaign',
    columns: [
      {
        className: 'col-4',
        controls: [
          {
            id: 'suspicious-campaign.min-numbers-in-window',
            label: ' Minimum number of tests in the lookback window',
          },
          {
            id: 'suspicious-campaign.min-tests-for-check-delivery',
            label: 'Number of most recent tests to check for delivery',
          },
        ],
      },
      {
        className: 'col-4',
        controls: [
          {
            id: 'suspicious-campaign.lookback-window-offset',
            label: 'Lookback window (hours)',
          },
          {
            id: 'suspicious-campaign.test-ttl',
            label: 'TTL of the triggered test (minutes)',
          },

        ],
      },
      {
        className: 'col-4',
        controls: [
          {
            id: 'suspicious-campaign.min-distinct-users',
            label: 'Minimum distinct accounts required',
          },
          {
            id: 'suspicious-campaign.excluded-suppliers',
            label: 'Excluded suppliers',
          },
          {
            id: 'suspicious-campaign.excluded-users',
            label: 'Excluded users',
          },
        ],
      },
    ]
  },
  {
    title: 'Suspicious campaign (problematic countries)',
    columns: [
      {
        className: 'col-4',
        controls: [
          {
            id: 'suspicious-campaign.problematic-countries-min-numbers-in-window',
            label: ' Minimum number of tests in the lookback window',
          },
          {
            id: 'suspicious-campaign.problematic-countries-min-tests-for-check-delivery',
            label: 'Number of most recent tests to check for delivery',
          },
        ],
      },
      {
        className: 'col-4',
        controls: [
          {
            id: 'suspicious-campaign.problematic-countries-lookback-window-offset',
            label: 'Lookback window (hours)',
          },
          {
            id: 'suspicious-campaign.problematic-countries',
            label: 'Countries (MCC)',
          },

        ],
      },
      {
        className: 'col-4',
        controls: [
          {
            id: 'suspicious-campaign.problematic-countries-min-distinct-users',
            label: 'Minimum distinct accounts required',
          },
        ],
      },
    ]
  },
  {
    title: 'Deactivation campaign',
    columns: [
      {
        className: 'col-4',
        controls: [
          {
            id: 'deactivation-campaign.last-sms-offset',
            label: 'Period for number deactivation (hours)',
          },
        ],
      },
    ],
  },
  {
    title: 'Blocking algorithm',
    columns: [
      {
        className: 'col-4',
        controls: [
          {
            id: 'phone-blocker.wi-rules.1.numbers',
            label: 'Numbers threshold level 1',
          },
          {
            id: 'phone-blocker.wi-rules.1.wi',
            label: 'WI threshold level 1',
          },
        ],
      },
      {
        className: 'col-4',
        controls: [
          {
            id: 'phone-blocker.wi-rules.2.numbers',
            label: 'Numbers threshold level 2',
          },
          {
            id: 'phone-blocker.wi-rules.2.wi',
            label: 'WI threshold level 2',
          },
        ],
      },
      {
        className: 'col-4',
        controls: [
          {
            id: 'phone-blocker.wi-rules.3.numbers',
            label: 'Numbers threshold level 3',
          },
          {
            id: 'phone-blocker.wi-rules.3.wi',
            label: 'WI threshold level 3',
          },
        ],
      },
    ],
  },
];
